import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { CalendarClock } from "lucide-react";

import { UICard, UIChart } from "../../ui";
import { clx } from "../../../utils/Helpers";
import DashboardEmptyState from "../DashboardEmptyState";
import { CHART_SEGMENT_ORDER, STATUS_COLORS } from "./constants";
import "../dashboardSection.scss";
import "./TotalEventsChart.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");

/**
 * Dashboard "Events" card — donut of events by status with a legend, or an
 * empty state when the company has no events.
 */
const TotalEventsChart = ({
  title,
  totalLabel,
  total,
  breakdown,
  emptyTitle,
  emptyDescription,
  classNames
}) => {
  const rows = useMemo(
    () =>
      breakdown.map((item) => ({
        id: item.id,
        label: item.label,
        value: Number(item.value) || 0,
        color: STATUS_COLORS[item.id] || "var(--color-primary-300)",
      })),
    [breakdown],
  );

  const chartData = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          CHART_SEGMENT_ORDER.indexOf(a.id) - CHART_SEGMENT_ORDER.indexOf(b.id),
      ),
    [rows],
  );

  const hasData = Number(total) > 0 && rows.some((row) => row.value > 0);

  return (
    <UICard title={title} className="dashboard-card total-events-chart">
      <div className="total-events-chart__content">
        {hasData ? (
          <>
            <p className="total-events-chart__total">
              <span className="total-events-chart__total-value">
                {formatCount(total)}
              </span>
              <span className="total-events-chart__total-label">
                {totalLabel}
              </span>
            </p>

            <div
              className={clx(
                "total-events-chart__body",
                classNames.body && `total-events-chart__body--${classNames.body}`,
              )}
            >
              <UIChart
                className="total-events-chart__donut"
                data={chartData}
                size={120}
                thickness={18}
                gap={2}
                ariaLabel={title}
              />
              <ul className="total-events-chart__legend">
                {rows.map((row) => (
                  <li key={row.id} className="total-events-chart__legend-item">
                    <span
                      className="total-events-chart__legend-dot"
                      style={{ background: row.color }}
                    />
                    <span className="total-events-chart__legend-text">
                      {row.label}
                    </span>
                    <span className="total-events-chart__legend-text">
                      {formatCount(row.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <DashboardEmptyState
            icon={<CalendarClock />}
            title={emptyTitle}
            description={emptyDescription}
          />
        )}
      </div>
    </UICard>
  );
};

TotalEventsChart.propTypes = {
  title: PropTypes.string.isRequired,
  totalLabel: PropTypes.string.isRequired,
  total: PropTypes.number,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,

  classNames: PropTypes.shape({
    body: PropTypes.string,
  }),
};

TotalEventsChart.defaultProps = {
  total: 0,
  breakdown: [],
  classNames: {
    body: "",
  },
};

export default TotalEventsChart;
